import { useMeQuery } from 'api/AuthApi';
import { useGetGiftsQuery } from 'api/GiftsApi';
import { useGetAllOrganizationInvitesQuery } from 'api/OrganizationInvitesApi';
import { UserStatusEnum } from 'api/UsersApi';
import HipaaAcceptModal, { HIPAA_ACCEPT_MODAL_NAME } from 'components/Modals/HipaaAcceptModal/HipaaAcceptModal';
import HipaaRevokeModal from 'components/Modals/HipaaRevokeModal/HipaaRevokeModal';
import ReceivedGiftModal, { RECEIVED_GIFT_MODAL_NAME } from 'components/Modals/ReceivedGiftModal/ReceivedGiftModal';
import WarningDeceasedStatusModal, {
  WARNING_DECEASED_STATUS_MODAL_NAME
} from 'components/Modals/WarningDeceasedStatusModal/WarningDeceasedStatusModal';
import PatientArtifactsPage from 'pages/Artifacts/PatientArtifactsPage';
import PublicArtifactCollectionPage from 'pages/Artifacts/PublicArtifactCollectionPage';
import Beneficiaries from 'pages/Admin/Beneficiaries/Beneficiaries';
import Beneficiary from 'pages/Admin/Beneficiary/Beneficiary';
import BeneficiaryMessage from 'pages/Admin/BeneficiaryMessage/BeneficiaryMessage';
import ConfirmEmail from 'pages/Auth/ConfirmEmail/ConfirmEmail';
import ForgotPassword from 'pages/Auth/ForgotPassword/ForgotPassword';
import Login from 'pages/Auth/Login/Login';
import ResetPassword from 'pages/Auth/ResetPassword/ResetPassword';
import SignUp from 'pages/Auth/SignUp/SignUp';
import Account from 'pages/Client/Account/Account';
import Billing from 'pages/Client/Billing/Billing';
import Chat from 'pages/Client/Chat/Chat';
import Contacts from 'pages/Client/Contacts/Contacts';
import CreateMessage from 'pages/Client/CreateMessage/CreateMessage';
import Dashboard from 'pages/Client/Dashboard/Dashboard';
import Home from 'pages/Client/Home/Home';
import MessageTemplates from 'pages/Client/MessageTemplates/MessageTemplates';
import Pricing from 'pages/Client/Pricing/Pricing';
import SelectCreateMessage from 'pages/Client/SelectCreateMessage/SelectCreateMessage';
import SharedArtifactCollectionPage from 'pages/Artifacts/SharedArtifactCollectionPage';
import SharedWithMe from 'pages/Client/SharedWithMe/SharedWithMe';
import AffiliateLink from 'pages/Common/AffiliateLink/AffiliateLink';
import ContactUs from 'pages/Common/ContactUs/ContactUs';
import Faq from 'pages/Common/Faq/Faq';
import { GiftPage } from 'pages/Gift/GiftPage';
import { LifeStorySharePage } from 'pages/LifeStory/LifeStorySharePage';
import { LifeReviewPage } from 'pages/LifeReview/LifeReviewPage';
import { LifeReviewSessionPage } from 'pages/LifeReview/LifeReviewSessionPage';
import { MessagePage } from 'pages/Messages/MessagePage';
import SysAdminGifts from 'pages/SysAdmin/Gifts/Gifts';
import SysAdminPayments from 'pages/SysAdmin/Payments/Payments';
import SysAdminReport from 'pages/SysAdmin/Report/Report';
import SysAdminReportUser from 'pages/SysAdmin/ReportUser/ReportUser';
import PatientProfileDetails from 'pages/SysAdmin/PatientProfileDetails/PatientProfileDetails';
import SysAdminUserDetails from 'pages/SysAdmin/UserDetails/UserDetails';
import SysAdminUsers from 'pages/SysAdmin/Users/Users';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { selectAuthToken } from 'store/authSlice';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { shouldRequireHipaaAcceptance } from 'utils/identityGuards';
import {
  isArtifactPublicContext,
  isArtifactPublicHash,
  isLifeStoryPublicContext,
  isLifeStoryPublicHash
} from 'utils/publicRoutePrivacy';
import { sessionActivity } from 'utils/sessionActivity';
import PrivateRoutes from 'utils/Router/PrivateRoutes';
import UnauthorizedRoutes from 'utils/Router/UnauthorizedRoutes';
import './App.css';

const TRACKER_GTM_SCRIPT_ID = 'ea-gtm-script';
const TRACKER_FACEBOOK_SCRIPT_ID = 'ea-facebook-sdk-script';
const TENANT_REDIRECT_GUARD_PREFIX = 'tenant-redirect:v1';
const TENANT_REDIRECT_GUARD_TTL_MS = 10 * 1000;
const isNonProductionEnv = process.env.NODE_ENV !== 'production';

const appendScriptOnce = (id: string, src: string) => {
  if (typeof document === 'undefined' || document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};

const removeScript = (id: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.getElementById(id)?.remove();
};

const normalizeOrigin = (domain: string): string | null => {
  if (!domain) {
    return null;
  }

  try {
    return new URL(domain).origin;
  } catch (_) {
    return null;
  }
};

const buildTenantRedirectGuardKey = (targetOrigin: string, pathname: string, search: string, hash: string): string =>
  `${TENANT_REDIRECT_GUARD_PREFIX}:${targetOrigin}:${pathname}${search}${hash}`;

const readTenantRedirectGuardTimestamp = (guardKey: string): number | null => {
  try {
    const rawTimestamp = window.sessionStorage.getItem(guardKey);
    if (!rawTimestamp) {
      return null;
    }

    const parsedTimestamp = Number(rawTimestamp);
    if (!Number.isFinite(parsedTimestamp)) {
      return null;
    }

    return parsedTimestamp;
  } catch (_) {
    return null;
  }
};

const writeTenantRedirectGuardTimestamp = (guardKey: string, timestamp: number): void => {
  try {
    window.sessionStorage.setItem(guardKey, String(timestamp));
  } catch (_) {
    // no-op when storage is unavailable
  }
};

const logTenantRedirectDiagnostics = (diagnostics: Record<string, unknown>): void => {
  if (!isNonProductionEnv) {
    return;
  }

  console.info('[auth][tenant-redirect]', diagnostics);
};

function App() {
  const dispatch = useAppDispatch();
  const { pathname, hash, search } = useLocation();

  const isLifeStoryHashContext = isLifeStoryPublicHash(hash);
  const isArtifactHashContext = isArtifactPublicHash(hash);
  const isPublicTokenContext =
    isLifeStoryPublicContext({ pathname, hash, search }) || isArtifactPublicContext({ pathname, hash, search });
  const isPublicHashContext = isLifeStoryHashContext || isArtifactHashContext;
  const accessToken = useSelector(selectAuthToken);

  const {
    refetch: refetchMe,
    data: user,
    isSuccess: isMeSuccess
  } = useMeQuery(undefined, { skip: isPublicTokenContext || !accessToken });
  const { data: organizationInvites } = useGetAllOrganizationInvitesQuery(undefined, {
    skip:
      isPublicTokenContext ||
      !accessToken ||
      !user ||
      Boolean(user.tenantOrganizationId) ||
      !user.tenantDomain
  });
  const { data: gifts } = useGetGiftsQuery(undefined, { skip: !user });
  const [tenantRedirectSettled, setTenantRedirectSettled] = useState(false);

  useEffect(() => {
    if (isPublicTokenContext || !accessToken) {
      setTenantRedirectSettled(true);
      return;
    }

    setTenantRedirectSettled(false);
  }, [accessToken, isPublicTokenContext]);

  useEffect(() => {
    if (isPublicTokenContext) {
      removeScript(TRACKER_GTM_SCRIPT_ID);
      removeScript(TRACKER_FACEBOOK_SCRIPT_ID);
      return;
    }

    const gtmId = process.env.REACT_APP_GTM_ID;
    if (gtmId) {
      window.dataLayer = window.dataLayer || [];
      const gtmParams = new URLSearchParams({ id: gtmId });
      const gtmAuthId = process.env.REACT_APP_GTM_AUTH_ID;
      const gtmEnvId = process.env.REACT_APP_GTM_ENV_ID;
      if (gtmAuthId) {
        gtmParams.set('gtm_auth', gtmAuthId);
      }
      if (gtmEnvId) {
        gtmParams.set('gtm_preview', gtmEnvId);
      }
      if (gtmAuthId || gtmEnvId) {
        gtmParams.set('gtm_cookies_win', 'x');
      }
      appendScriptOnce(TRACKER_GTM_SCRIPT_ID, `https://www.googletagmanager.com/gtm.js?${gtmParams.toString()}`);
    }

    appendScriptOnce(TRACKER_FACEBOOK_SCRIPT_ID, 'https://connect.facebook.net/en_US/sdk.js');
  }, [isPublicTokenContext]);

  useEffect(() => {
    const hasStableMeResolution = isMeSuccess && Boolean(user);

    if (isPublicTokenContext || !accessToken || !tenantRedirectSettled || !hasStableMeResolution) {
      return;
    }

    let isSessionActivityProtected = sessionActivity.isProtected();
    let timer = window.setInterval(refetchMe, isSessionActivityProtected ? 60 * 1000 : 5 * 60 * 1000);

    const restartTimer = () => {
      window.clearInterval(timer);
      timer = window.setInterval(refetchMe, isSessionActivityProtected ? 60 * 1000 : 5 * 60 * 1000);
    };

    const unsubscribe = sessionActivity.subscribe((isProtected) => {
      if (isSessionActivityProtected === isProtected) {
        return;
      }

      const wasProtected = isSessionActivityProtected;
      isSessionActivityProtected = isProtected;
      restartTimer();

      if (wasProtected && !isProtected) {
        refetchMe();
      }
    });

    return () => {
      unsubscribe();
      window.clearInterval(timer);
    };
  }, [accessToken, isPublicTokenContext, isMeSuccess, refetchMe, tenantRedirectSettled, user]);

  useEffect(() => {
    if (!accessToken || isPublicTokenContext || !user) {
      return;
    }

    const currentOrigin = window.location.origin;
    const normalizedCurrentOrigin = normalizeOrigin(currentOrigin);
    const normalizedTenantOrigin = normalizeOrigin(user.tenantDomain);
    const hasAcceptedOrgMembership = Boolean(user.tenantOrganizationId);
    const hasPendingInvite = Boolean(organizationInvites?.length);
    const canEvaluateTenantRedirect = Boolean(user.tenantDomain && normalizedCurrentOrigin && normalizedTenantOrigin);
    const shouldRedirectToTenant =
      canEvaluateTenantRedirect &&
      hasAcceptedOrgMembership &&
      normalizedTenantOrigin !== normalizedCurrentOrigin;
    const targetLocationPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const guardKey = normalizedTenantOrigin
      ? buildTenantRedirectGuardKey(normalizedTenantOrigin, window.location.pathname, window.location.search, window.location.hash)
      : null;
    const now = Date.now();
    const lastRedirectAt = guardKey ? readTenantRedirectGuardTimestamp(guardKey) : null;
    const guardAgeMs = lastRedirectAt === null ? null : Math.max(0, now - lastRedirectAt);
    const isGuardHit = guardAgeMs !== null && guardAgeMs < TENANT_REDIRECT_GUARD_TTL_MS;

    const diagnostics = {
      currentOrigin,
      tenantDomain: user.tenantDomain,
      normalizedCurrentOrigin,
      normalizedTenantOrigin,
      hasAcceptedOrgMembership,
      hasPendingInvite,
      guardKey,
      guardTtlMs: TENANT_REDIRECT_GUARD_TTL_MS,
      guardAgeMs,
      guardHit: isGuardHit,
      tenantRedirectSettled,
      redirectFired: false,
      reason: 'tenant-redirect-not-evaluated'
    };

    if (!canEvaluateTenantRedirect && user.tenantDomain) {
      diagnostics.reason = 'tenant-domain-normalization-failed';
    } else if (!hasAcceptedOrgMembership && user.tenantDomain) {
      diagnostics.reason = hasPendingInvite
        ? 'skip-tenant-redirect-pending-invite-only-user'
        : 'skip-tenant-redirect-user-without-accepted-org-membership';
    } else if (canEvaluateTenantRedirect && normalizedTenantOrigin === normalizedCurrentOrigin) {
      diagnostics.reason = 'skip-tenant-redirect-already-on-tenant-origin';
    } else if (shouldRedirectToTenant && normalizedTenantOrigin) {
      if (isGuardHit) {
        diagnostics.reason = 'skip-tenant-redirect-session-ttl-guard-hit';
      } else {
        diagnostics.reason = 'redirect-to-accepted-tenant-origin';
        diagnostics.redirectFired = true;

        if (guardKey) {
          writeTenantRedirectGuardTimestamp(guardKey, now);
        }

        logTenantRedirectDiagnostics(diagnostics);
        window.location.replace(`${normalizedTenantOrigin}${targetLocationPath}`);
        return;
      }
    }

    logTenantRedirectDiagnostics(diagnostics);
    setTenantRedirectSettled(true);

    if (shouldRequireHipaaAcceptance(user)) {
      dispatch(openModal({ name: HIPAA_ACCEPT_MODAL_NAME }));
    } else if (user.status === UserStatusEnum.UNVERIFIED_DECEASED || user.status === UserStatusEnum.DECEASED) {
      dispatch(openModal({ name: WARNING_DECEASED_STATUS_MODAL_NAME }));
    }
  }, [accessToken, dispatch, isPublicTokenContext, organizationInvites, tenantRedirectSettled, user]);

  useEffect(() => {
    if (user?.hipaaStatus === 'accepted' && user?.status === UserStatusEnum.ACTIVE && gifts?.length) {
      dispatch(openModal({ name: RECEIVED_GIFT_MODAL_NAME, data: { gift: gifts[0] } }));
    }
  }, [dispatch, user, gifts]);

  return (
    <div className="App">
      <Routes>
        <Route path="/messages/:messageContactId" element={<MessagePage />} />
        <Route path="/public/messages/contacts/:token" element={<MessagePage />} />
        <Route path="/life-story" element={<LifeStorySharePage />} />
        <Route path="/life-story/:token" element={<LifeStorySharePage />} />
        <Route path="/public/life-story" element={<LifeStorySharePage />} />
        <Route path="/public/life-story/:token" element={<LifeStorySharePage />} />
        <Route path="/artifacts" element={<PublicArtifactCollectionPage />} />
        <Route path="/artifacts/:token" element={<PublicArtifactCollectionPage />} />
        <Route path="/public/artifacts" element={<PublicArtifactCollectionPage />} />
        <Route path="/public/artifacts/:token" element={<PublicArtifactCollectionPage />} />
        <Route path="/life-review/invite" element={<LifeStorySharePage />} />
        <Route path="/life-review/invite/:token" element={<LifeStorySharePage />} />
        <Route path="/life-review/share" element={<LifeStorySharePage />} />
        <Route path="/life-review/share/:token" element={<LifeStorySharePage />} />
        <Route path="/partner/:affiliateCode" element={<AffiliateLink />} />
        <Route path="/gift" element={<GiftPage />} />

        {isLifeStoryHashContext && <Route path="*" element={<LifeStorySharePage />} />}
        {isArtifactHashContext && <Route path="*" element={<PublicArtifactCollectionPage />} />}

        <Route element={<UnauthorizedRoutes />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        <Route element={<PrivateRoutes />}>
          {!isPublicHashContext && <Route path="/" element={<Navigate to="/home" replace />} />}
          <Route path="/home" element={<Home />} />
          <Route path="/app/artifacts" element={<PatientArtifactsPage />} />
          <Route path="/app/life-story" element={<LifeReviewPage />} />
          <Route path="/app/shared-with-me/life-stories/:inviteId" element={<LifeStorySharePage />} />
          <Route path="/chat/:chatId" element={<Chat />} />
          <Route path="/message" element={<SelectCreateMessage />} />
          <Route path="/message/:messageId" element={<CreateMessage />} />
          <Route path="/life-review" element={<LifeReviewPage />} />
          <Route path="/life-review/session" element={<LifeReviewSessionPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/shared-with-me" element={<SharedWithMe />} />
          <Route path="/app/shared-with-me/artifacts/:shareId" element={<SharedArtifactCollectionPage />} />
          <Route path="/inbox" element={<SharedWithMe />} />
          <Route path="/beneficiaries" element={<Beneficiaries />} />
          <Route path="/beneficiaries/:adminAccessId" element={<Beneficiary />} />
          <Route path="/beneficiaries/:adminAccessId/message/:messageId" element={<BeneficiaryMessage />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/message-templates" element={<MessageTemplates />} />
          <Route path="/account" element={<Account />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/contact-us" element={<ContactUs />} />

          <Route path="/sysadmin">
            <Route index element={<Navigate to="/sysadmin/users" />} />
            <Route path="users" element={<SysAdminUsers />} />
            <Route path="users/:userId" element={<SysAdminUserDetails />} />
            <Route path="patient-profiles/:patientProfileId" element={<PatientProfileDetails />} />
            <Route path="payments" element={<SysAdminPayments />} />
            <Route path="gifts" element={<SysAdminGifts />} />
            <Route path="report" element={<SysAdminReport />} />
            <Route path="report/:userId" element={<SysAdminReportUser />} />
          </Route>
        </Route>
      </Routes>

      <ReceivedGiftModal />
      <HipaaAcceptModal />
      <HipaaRevokeModal />
      <WarningDeceasedStatusModal />
      {/*<FloatTimerComponent firstLoginDate={user?.firstLoginDate || null} />*/}
    </div>
  );
}

export default App;
